// S4 (v0.15.0) — MFA settings (enroll, verify, disable, regen).
import { useState } from "react";
import { CheckCircle2Icon, ShieldIcon, CopyIcon } from "lucide-react";

import { api } from "@/api/client";
import {
  useMFADisable,
  useMFAEnroll,
  useMFARegenerateBackupCodes,
  useMFAVerifyEnrollment,
} from "@/api/s4";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { useQuery } from "@tanstack/react-query";

interface Me {
  id: string;
  email: string;
  is_mfa_enabled: boolean;
}

export default function MFASettingsPage() {
  const { data: me, refetch } = useQuery<Me>({
    queryKey: ["auth", "me"],
    queryFn: async () => (await api.get<Me>("/api/v1/auth/me")).data,
  });
  const enroll = useMFAEnroll();
  const verify = useMFAVerifyEnrollment();
  const disable = useMFADisable();
  const regenerate = useMFARegenerateBackupCodes();

  const [qr, setQr] = useState<string | null>(null);
  const [uri, setUri] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [totpCode, setTotpCode] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [disableOpen, setDisableOpen] = useState(false);
  const [disableTotp, setDisableTotp] = useState("");
  const [disablePassword, setDisablePassword] = useState("");

  const [regenOpen, setRegenOpen] = useState(false);
  const [regenTotp, setRegenTotp] = useState("");
  const [regenCodes, setRegenCodes] = useState<string[] | null>(null);

  async function handleEnable() {
    setError(null);
    try {
      const r = await enroll.mutateAsync();
      setQr(r.qr_code_base64);
      setUri(r.totp_uri);
      setBackupCodes(r.backup_codes);
    } catch (err) {
      setError(extractDetail(err) ?? "Could not start MFA enrollment");
    }
  }

  async function handleVerifyEnrollment() {
    setError(null);
    try {
      await verify.mutateAsync({ totp_code: totpCode });
      setTotpCode("");
      setQr(null);
      setUri(null);
      // keep backupCodes visible — user needs them post-enroll
      await refetch();
    } catch (err) {
      setError(extractDetail(err) ?? "Invalid code");
    }
  }

  async function handleDisable() {
    setError(null);
    try {
      await disable.mutateAsync({
        totp_code: disableTotp,
        current_password: disablePassword,
      });
      setDisableOpen(false);
      setDisableTotp("");
      setDisablePassword("");
      await refetch();
    } catch (err) {
      setError(extractDetail(err) ?? "Failed to disable MFA");
    }
  }

  async function handleRegenerate() {
    setError(null);
    try {
      const r = await regenerate.mutateAsync({ totp_code: regenTotp });
      setRegenCodes(r.backup_codes);
      setRegenTotp("");
    } catch (err) {
      setError(extractDetail(err) ?? "Failed");
    }
  }

  if (!me) return <p className="text-sm">Loading…</p>;

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div className="flex items-center gap-3">
        <ShieldIcon className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            MFA — Two-Factor Authentication
          </h1>
          <p className="text-sm text-muted-foreground">
            Add an extra layer of security to your account.
          </p>
        </div>
      </div>

      {error && (
        <p className="rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-600">
          {error}
        </p>
      )}

      {/* State 3 — enrolled */}
      {me.is_mfa_enabled && !qr && (
        <Card className="border-emerald-500/30">
          <CardContent className="space-y-3 p-4">
            <p className="flex items-center gap-2 text-base font-semibold text-emerald-700">
              <CheckCircle2Icon className="h-5 w-5" />
              Two-factor authentication is enabled
            </p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="destructive"
                onClick={() => setDisableOpen(true)}
              >
                Disable MFA
              </Button>
              <Button
                variant="outline"
                onClick={() => setRegenOpen(true)}
              >
                Regenerate Backup Codes
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* State 1 — not enrolled */}
      {!me.is_mfa_enabled && !qr && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <p className="text-sm">
              Once enabled, you'll need a 6-digit code from your
              authenticator app every time you sign in.
            </p>
            <Button onClick={handleEnable} disabled={enroll.isPending}>
              Enable Two-Factor Authentication
            </Button>
          </CardContent>
        </Card>
      )}

      {/* State 2 — enrolling */}
      {qr && (
        <Card>
          <CardContent className="space-y-4 p-4">
            <div>
              <p className="text-sm font-medium">
                Scan this QR code with your authenticator app
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Supported: Google Authenticator, Authy, Microsoft Authenticator.
              </p>
            </div>
            <img
              src={`data:image/png;base64,${qr}`}
              alt="MFA QR code"
              className="h-44 w-44 rounded border border-border bg-white p-2"
            />
            {uri && (
              <details className="text-xs">
                <summary className="cursor-pointer text-muted-foreground">
                  Or enter the secret manually
                </summary>
                <p className="mt-1 break-all rounded bg-muted/40 p-2 font-mono">
                  {uri}
                </p>
              </details>
            )}
            <div>
              <Label>6-digit code</Label>
              <Input
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                inputMode="numeric"
                pattern="\d{6}"
                autoFocus
                maxLength={6}
                placeholder="123456"
              />
            </div>
            <Button onClick={handleVerifyEnrollment} disabled={verify.isPending}>
              Verify and Enable
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Backup codes shown once after enroll or regen */}
      {backupCodes && (
        <BackupCodesCard codes={backupCodes} onAck={() => setBackupCodes(null)} />
      )}
      {regenCodes && (
        <BackupCodesCard codes={regenCodes} onAck={() => setRegenCodes(null)} />
      )}

      {/* Modals */}
      {disableOpen && (
        <Card className="border-red-500/30">
          <CardContent className="space-y-3 p-4">
            <p className="font-semibold">Disable MFA</p>
            <p className="text-sm text-muted-foreground">
              Enter your current TOTP code and password to confirm.
            </p>
            <div>
              <Label>TOTP code</Label>
              <Input
                value={disableTotp}
                onChange={(e) => setDisableTotp(e.target.value)}
                maxLength={6}
              />
            </div>
            <div>
              <Label>Current password</Label>
              <PasswordInput
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setDisableOpen(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDisable}>
                Disable MFA
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {regenOpen && (
        <Card>
          <CardContent className="space-y-3 p-4">
            <p className="font-semibold">Regenerate Backup Codes</p>
            <p className="text-sm text-muted-foreground">
              Enter your current TOTP code. The previous backup codes will
              stop working.
            </p>
            <div>
              <Label>TOTP code</Label>
              <Input
                value={regenTotp}
                onChange={(e) => setRegenTotp(e.target.value)}
                maxLength={6}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setRegenOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleRegenerate}>Generate New Codes</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function BackupCodesCard({
  codes,
  onAck,
}: {
  codes: string[];
  onAck: () => void;
}) {
  function copyAll() {
    navigator.clipboard.writeText(codes.join("\n"));
  }
  return (
    <Card className="border-amber-500/30">
      <CardContent className="space-y-3 p-4">
        <p className="font-semibold text-amber-700">
          Save your backup codes
        </p>
        <p className="text-sm text-muted-foreground">
          These codes can be used once each if you lose access to your
          authenticator. They will not be shown again.
        </p>
        <div className="grid grid-cols-2 gap-2 font-mono text-sm md:grid-cols-4">
          {codes.map((c) => (
            <code
              key={c}
              className="rounded border border-border bg-muted/40 px-2 py-1"
            >
              {c}
            </code>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={copyAll}>
            <CopyIcon className="mr-1 h-3 w-3" />
            Copy All
          </Button>
          <Button size="sm" onClick={onAck}>
            I have saved these
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function extractDetail(err: unknown): string | null {
  const e = err as { response?: { data?: { detail?: string } } };
  return typeof e.response?.data?.detail === "string"
    ? e.response.data.detail
    : null;
}
